import { PaginatedResponse, restAPI } from '../rest-api';

import { FinalReportData } from '~/interfaces/FinalReportData';
import { FinalReportTemplate } from '~/interfaces/FinalReportTemplate';
import { FinalReportItemInterface } from '~/slices/finalReportSlice';

interface CombinedFinalReportData
  extends FinalReportData,
  FinalReportItemInterface { }

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    templates: builder.query<FinalReportTemplate[], { slug: string }>({
      query: ({ slug }) => ({
        url: `/tenant/${slug}/final-report-templates-list`,
      }),
      providesTags: (res) =>
        res ? [{ type: 'Reports', id: 'final-report-templates' }] : [],
    }),
    createTemplate: builder.mutation<
      string,
      {
        slug: string;
        template: FinalReportTemplate;
      }
    >({
      query: ({ slug, template }) => ({
        url: `/tenant/${slug}/final-report-templates`,
        method: 'POST',
        data: template,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'final-report-templates' }] : [],
    }),
    editTemplate: builder.mutation<
      string,
      {
        slug: string;
        id: number;
        template: FinalReportTemplate;
      }
    >({
      query: ({ slug, id, template }) => ({
        url: `/tenant/${slug}/final-report-templates/${id}`,
        method: 'PATCH',
        data: template,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'final-report-templates' }] : [],
    }),
    deleteTemplate: builder.mutation<
      string,
      {
        slug: string;
        id: number;
      }
    >({
      query: ({ slug, id }) => ({
        url: `/tenant/${slug}/final-report-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Reports', id: 'final-report-templates' }],
    }),
    finalReportList: builder.query<
      PaginatedResponse<FinalReportData>,
      { slug: string; case_manager_reference_id?: string }
    >({
      query: ({ slug, case_manager_reference_id }) => ({
        url: `/tenant/${slug}/final-reports-list`,
        params: {
          case_manager_reference_id,
        },
      }),
      providesTags: (res) =>
        res ? [{ type: 'Reports', id: 'final-report-list' }] : [],
    }),
    finalReportDetails: builder.query<
      CombinedFinalReportData,
      { slug: string; id: number }
    >({
      query: ({ slug, id }) => ({
        url: `/tenant/${slug}/final-reports/${id}`,
      }),
    }),
    createFinalReport: builder.mutation<
      FinalReportData,
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      { slug: string; finalReport: Record<string, any> }
    >({
      query: ({ slug, finalReport }) => ({
        url: `/tenant/${slug}/final-reports`,
        method: 'POST',
        data: finalReport,
      }),
      invalidatesTags: [{ type: 'Reports', id: 'final-report-list' }, { type: 'Reports', id: 'report-details' }],
    }),
    updateFinalReport: builder.mutation<
      FinalReportData,
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      { slug: string; id: number; finalReport: Record<string, any> }
    >({
      query: ({ slug, id, finalReport }) => ({
        url: `/tenant/${slug}/final-reports/${id}`,
        method: 'PATCH',
        data: finalReport,
      }),
      invalidatesTags: [{ type: 'Reports', id: 'final-report-list' }, { type: 'Reports', id: 'report-details' }],
    }),
    deleteFinalReport: builder.mutation<string, { slug: string; id: number }>({
      query: ({ slug, id }) => ({
        url: `/tenant/${slug}/final-reports/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Reports', id: 'final-report-list' }],
    }),
  }),
  overrideExisting: false,
});

export const FinalReportService = endpoints;
